import { InputField } from 'components/Global';
import SelectFieldDropdown from 'components/Global/SelectFieldDropdown';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ReactComponent as PlusCircleIcon } from 'icons/plus-icon-circle-blue.svg';
import { ReactComponent as Xsign } from 'icons/x-sign-icon.svg';
import SelectFieldTime from 'components/Global/SelectFieldTime';
import { useAppContext } from 'components/Context/AppContext';
import { deleteGarageAdditionalSettings, getGarageAdditionalSettings } from 'features/garages/actions';
import areIntervalsOverlapping from 'date-fns/areIntervalsOverlapping';

const TableOperationalSetting = ({
  locationId,
  data,
  setData,
  categoryData,
  numberInputOnWheelPreventChange,
  handleKeyDown,
  selectedService,
}) => {
  const dispatch = useDispatch();

  const { data: allCategories } = useSelector((state) => state.category);
  const { data: rentalLocation } = useSelector((state) => state.rentalLocation);

  const [invalidTime, setInvalidTime] = useState([]);

  const { showToast, setShowConfirmation } = useAppContext();

  const addOperationalSettingHandler = () => {
    if (!selectedService.service) {
      return showToast({ type: 'error', message: 'Harap memilih Tipe Sewa' });
    }

    const newData = {
      id: Math.random() + 1 + '', // temp id
      fee: '',
      status: false,
      service: selectedService.service,
      value: selectedService.value,
      category_ids: [],
      start_time: '',
      end_time: '',
      added: true,
    };

    setData((prevState) => [...prevState, newData]);
  };

  const checkExistingCategory = (arr1, arr2) => arr1.some((obj1) => arr2.some((obj2) => obj1['id'] === obj2['id']));

  const removeOperationalSettingHandler = async (setting) => {
    if (setting.added) {
      setData((prev) => prev.filter((item) => item.id !== setting.id));
    } else {
      setShowConfirmation({
        message: 'Apakah anda yakin ingin menghapus Setting Operasional ini?',
        show: true,
        onClick: async () => {
          try {
            await dispatch(deleteGarageAdditionalSettings(setting.id)).unwrap();
            showToast({ type: 'success', message: 'Berhasil Menghapus Setting Operasional' });
          } catch (error) {
            // eslint-disable-next-line no-console
            console.log(error);
            showToast({ type: 'error', message: 'Gagal Menghapus Setting Operasional' });
          } finally {
            dispatch(getGarageAdditionalSettings(setting.garage_id));
          }
        },
      });
    }
  };

  const handleEditStatus = (status, itemData) => {
    if (status) {
      let isInvalidTime = [];
      const validCategory = itemData.category_ids.length !== 0;
      const validStartTime = itemData.start_time !== '';
      const validEndTime = itemData.end_time !== '';
      const validPenaltyFee = +itemData.fee !== 0;

      if (!validCategory) {
        return showToast({ type: 'error', message: 'Pilih Kategori Mobil' });
      }

      if (!validStartTime) {
        return showToast({ type: 'error', message: 'Pilih Jam Mulai Operasional' });
      }

      if (!validEndTime) {
        return showToast({ type: 'error', message: 'Pilih Jam Selesai Operasional' });
      }

      if (!validPenaltyFee) {
        return showToast({ type: 'error', message: 'Masukan Tarif Penalti' });
      }

      const filtered = data.filter((item) => item.start_time && item.end_time && item.fee && item.status);

      for (const [, savedData] of filtered.entries()) {
        const parsedStartTime = new Date(`1970-01-01T${itemData.start_time}:00`);
        const parsedEndTime = new Date(`1970-01-01T${itemData.end_time}:00`);
        const itemStartTime = new Date(`1970-01-01T${savedData.start_time}:00`);
        const itemEndTime = new Date(`1970-01-01T${savedData.end_time}:00`);

        const isExist = checkExistingCategory(savedData.category_ids, itemData.category_ids);

        const isOverlapping = areIntervalsOverlapping(
          { start: parsedStartTime, end: parsedEndTime },
          { start: itemStartTime, end: itemEndTime },
          { inclusive: true },
        );

        if (isOverlapping && isExist) {
          isInvalidTime.push(savedData);
        }
      }

      setInvalidTime(isInvalidTime);

      if (isInvalidTime.length > 0) {
        if (itemData.status) {
          setData((prev) =>
            prev.map((item) => (item.id === itemData.id ? { ...item, status: !itemData.status } : item)),
          );
        } else {
          showToast({ type: 'error', message: 'Data jam tidak boleh sama' });
        }
        return;
      }
      setData((prev) => prev.map((item) => (item.id === itemData.id ? { ...item, status } : item)));
    } else {
      setData((prev) => prev.map((item) => (item.id === itemData.id ? { ...item, status: false } : item)));
      setInvalidTime([]);
    }
  };

  const filterCategoryIds = (categoryIds, idToRemove) => categoryIds.filter((item) => item.id !== idToRemove);

  const handleSelectCategory = (id, status, itemData) => {
    const existedType = itemData.category_ids.find(
      (item) => item.id === allCategories.category?.find((el) => el.id == id)?.id,
    );

    if (existedType && status) return;
    const categoryValue = categoryData?.find((el) => el.id === id);

    if (status) {
      setData((prev) =>
        prev.map((item) =>
          item.id === itemData.id
            ? {
                ...item,
                category_ids: [
                  ...item.category_ids,
                  { id: categoryValue.id, name: categoryValue.name, value: categoryValue.name.toLowerCase() },
                ],
              }
            : item,
        ),
      );
    } else {
      setData((prev) =>
        prev.map((item) =>
          item.id === itemData.id
            ? {
                ...item,
                category_ids: filterCategoryIds(item.category_ids, id),
              }
            : item,
        ),
      );
    }
  };

  const handleSelectTime = (hour, minute, id, type) => {
    if (type === 'start') {
      setData((prev) =>
        prev.map((item) =>
          item.id === id
            ? {
                ...item,
                start_time: `${hour || '00'}:${minute || '00'}`,
              }
            : item,
        ),
      );
    } else if (type === 'end') {
      setData((prev) =>
        prev.map((item) =>
          item.id === id
            ? {
                ...item,
                end_time: `${hour || '00'}:${minute || '00'}`,
              }
            : item,
        ),
      );
    }
  };

  const handlePenaltyValue = (e, id) => {
    setData((prev) =>
      prev.map((item) =>
        item.id === id
          ? {
              ...item,
              fee: e.target.value,
            }
          : item,
      ),
    );
  };

  const selectedLocationCurrency = rentalLocation?.find((item) => item.id === +locationId)?.currency;

  return (
    <div className="garage-detail__operational-table-locations">
      <h3 style={{ fontSize: 12, marginBottom: 20, marginTop: 18 }}>Jam Operasional</h3>
      <div className="garage-detail__operational-table-locations__table-wrapper">
        <table className="table-content">
          <thead>
            <tr>
              <th>Nama Kategori Mobil</th>
              <th>Jam Mulai</th>
              <th>Jam Selesai</th>
              <th>Tarif Penalti</th>
              <th>Penalti Opsi</th>
            </tr>
          </thead>
          <tbody>
            {data?.length > 0 &&
              data.map((item) => (
                <tr key={item.id}>
                  <td>
                    <SelectFieldDropdown
                      placeholder="Pilih Kategori Mobil"
                      data={categoryData || []}
                      value={item.category_ids}
                      checkBox
                      onChange={(id, status) => handleSelectCategory(id, status, item)}
                    />
                  </td>
                  <td className={invalidTime.find((time) => time.id === item.id) ? 'invalid-time' : ''}>
                    <SelectFieldTime
                      placeholder="00:00"
                      value={item.start_time}
                      onChange={(hour, minute) => {
                        handleSelectTime(hour, minute, item.id, 'start');
                      }}
                      icon={false}
                      showAllHours={true}
                      disable={!selectedService?.service}
                    />
                  </td>
                  <td className={invalidTime.find((time) => time.id === item.id) ? 'invalid-time' : ''}>
                    <SelectFieldTime
                      placeholder="00:00"
                      value={item.end_time}
                      onChange={(hour, minute) => {
                        handleSelectTime(hour, minute, item.id, 'end');
                      }}
                      icon={false}
                      showAllHours={true}
                      disable={!selectedService?.service}
                    />
                  </td>
                  <td>
                    <InputField
                      placeholder={`${selectedLocationCurrency ?? 'Rp'} 0`}
                      className="location-wrapper__type-location"
                      value={item.fee ?? ''}
                      onChange={(e) => handlePenaltyValue(e, item.id)}
                      onWheel={numberInputOnWheelPreventChange}
                      onKeyDown={handleKeyDown}
                      type="number"
                      disable={!selectedService?.service}
                    />
                  </td>
                  <td>
                    <div className="penalty-status">
                      <div className="garage-detail__operational-status">
                        <div className="garage-detail__operational-status-info">
                          <div className="user__toggle-status">
                            <label className="switch">
                              <input
                                type="checkbox"
                                role={'switch'}
                                onChange={(e) => handleEditStatus(e.target.checked, item)}
                                checked={!selectedService?.outside_operational_status ? false : item?.status}
                              />
                              <span className="slider"></span>
                            </label>
                          </div>
                        </div>
                      </div>
                      <button className="penalty-status__remove" onClick={() => removeOperationalSettingHandler(item)}>
                        <Xsign />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan="5">
                <div className="add-button-wrapper">
                  <button onClick={addOperationalSettingHandler}>
                    <PlusCircleIcon />
                    <p>Tambah</p>
                  </button>
                </div>
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
};

export default TableOperationalSetting;
